import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Monitor, Smartphone, Tablet, Globe, Clock, Shield, LogOut, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDeviceIcon = (deviceType) => {
  switch (deviceType) {
    case 'Mobile':
      return <Smartphone className="w-5 h-5" strokeWidth={1.5} />;
    case 'Tablet':
      return <Tablet className="w-5 h-5" strokeWidth={1.5} />;
    default:
      return <Monitor className="w-5 h-5" strokeWidth={1.5} />;
  }
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatExpiresIn = (expiresAt) => {
  if (!expiresAt) return null;
  const expires = new Date(expiresAt);
  const now = new Date();
  const diffMs = expires - now;
  
  if (diffMs <= 0) return 'Expired';
  
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffDays > 1) return `Expires in ${diffDays} days`;
  if (diffHours > 1) return `Expires in ${diffHours} hours`;
  return 'Expires soon';
};

export const SessionsSection = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevokeSession = async (sessionId) => {
    setRevoking(sessionId);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session revoked successfully');
    } catch (error) {
      toast.error('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAllOther = async () => {
    setRevokingAll(true);
    try {
      const response = await axios.delete(`${API}/sessions`);
      setSessions(sessions.filter(s => s.is_current));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const otherSessions = sessions.filter(s => !s.is_current);

  return (
    <Card data-testid="sessions-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Active Sessions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Current Session */}
            {sessions.filter(s => s.is_current).map(session => (
              <div 
                key={session.id} 
                className="p-4 rounded-xl bg-emerald-50 border border-emerald-200"
                data-testid="current-session"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
                    {getDeviceIcon(session.device_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">
                        {session.browser} on {session.os}
                      </p>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">
                        <CheckCircle className="w-3 h-3" />
                        This device
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Globe className="w-3.5 h-3.5" />
                        {session.ip_address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(session.last_active)}
                      </span>
                    </div>
                    {session.remember_me && (
                      <p className="text-xs text-emerald-600 mt-1">
                        {session.expires_at ? formatExpiresIn(session.expires_at) : 'Extended session (30 days)'}
                      </p>
                    )}
                    {!session.remember_me && session.expires_at && (
                      <p className="text-xs text-gray-500 mt-1">{formatExpiresIn(session.expires_at)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Other Sessions */}
            {otherSessions.length > 0 && (
              <>
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700">Other devices</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRevokeAllOther}
                      disabled={revokingAll}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 text-xs"
                      data-testid="revoke-all-btn"
                    >
                      {revokingAll ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <LogOut className="w-3 h-3 mr-1" />
                      )}
                      Sign out all other devices
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {otherSessions.map(session => (
                      <div 
                        key={session.id} 
                        className="p-4 rounded-xl bg-gray-50 border border-gray-200"
                        data-testid="other-session"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
                            {getDeviceIcon(session.device_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">
                              {session.browser} on {session.os}
                            </p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Globe className="w-3.5 h-3.5" />
                                {session.ip_address}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {formatTimeAgo(session.last_active)}
                              </span>
                            </div>
                            {session.remember_me && (
                              <p className="text-xs text-gray-500 mt-1">
                                {session.expires_at ? formatExpiresIn(session.expires_at) : 'Extended session (30 days)'}
                              </p>
                            )}
                            {!session.remember_me && session.expires_at && (
                              <p className="text-xs text-gray-500 mt-1">{formatExpiresIn(session.expires_at)}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeSession(session.id)}
                            disabled={revoking === session.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`revoke-session-${session.id}`}
                          >
                            {revoking === session.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <LogOut className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {sessions.length === 0 && (
              <p className="text-center text-gray-500 py-4">No active sessions found</p>
            )}

            {sessions.length === 1 && sessions[0].is_current && (
              <p className="text-sm text-gray-500 text-center py-2">
                You're only signed in on this device.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionsSection;
